import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { Button, IconButton, Tooltip } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { InboxesPath } from 'entities/ExportRouters'
import { Contact } from 'entities/Contacts/model/Contact'
import { showCallDuringQuietHoursModalIfNeeded } from 'entities/QuietHours'

export const ConversationCall = observer(
  ({
    inboxId,
    conversationId,
    contactId,
    className,
  }: {
    inboxId: number
    conversationId: number
    contactId?: number
    className?: string
  }) => {
    const {
      callStore,
      inboxesStore,
      usersStore,
      organizationStore,
      featureFlagsStore,
      contactsStore,
    } = useEntitiesContext()
    const { isOutOfCredits, organization_call } = organizationStore
    const user = usersStore.user
    const inbox = inboxesStore.getItem(inboxId)

    const canManageInboxCalling = featureFlagsStore.settings_inboxes_manage_inbox_calling_v2
    const callerIdFailedReason = inbox?.type === 'inbox' && inbox?.callerIdFailedReason

    const isNumberNotVerifiedCallerId =
      inbox?.type === 'inbox' && inbox?.isNumberNotVerifiedCallerId

    const isNumberOutboundCalls = useMemo(() => {
      if (inbox?.type !== 'inbox') return false
      if (!inbox.number) {
        return false
      }
      if (!!inbox?.number?.use_organization_call_settings) {
        return organization_call?.is_active
      }

      return inbox.number.voice_outbound
    }, [inbox, organization_call])

    const isCallOutboundDisabled = !isNumberOutboundCalls

    const isCallDisabled =
      !!user?.isViewOnlyRole ||
      callStore.loading ||
      callStore.isBusy ||
      inbox?.isPFTInbox ||
      isOutOfCredits

    const ownerDesc = (
      <Button
        text='Enable it here'
        typeBtn='link'
        size='small'
        paddingInline='0'
        onClick={(event) => {
          event.preventDefault()

          const redirectPath = canManageInboxCalling
            ? InboxesPath.inboxCallingGeneral(inboxId)
            : `/settings/organization/inboxes/all-inboxes/${inboxId}/calling`

          uiStore.changeRouteViaVue({
            path: redirectPath,
          })
        }}
      />
    )

    const getTooltipLabel = () => {
      if (isOutOfCredits) {
        return {
          label: 'Your organization is out of message credits.',
          desc: 'Add message credits to continue calling.',
        }
      }
      if (!isCallDisabled && isCallOutboundDisabled) {
        if (
          user?.isAdminRole ||
          user?.isOwnerRole ||
          (inbox?.type === 'inbox' && inbox?.ownerId === user?.id)
        ) {
          return {
            label: 'Outbound calling is disabled',
            desc: ownerDesc,
          }
        }

        return { label: 'Outbound calling is disabled. Contact your admin to enable it.' }
      }

      if (callerIdFailedReason) {
        return {
          label: callerIdFailedReason,
        }
      }

      return {
        label: 'Call',
      }
    }

    return (
      <Tooltip {...getTooltipLabel()} type='description' placement='top'>
        <IconButton
          icon='phone'
          variant='icon'
          color='tertiary'
          transparentInactive
          disabled={isCallDisabled || isCallOutboundDisabled || isNumberNotVerifiedCallerId}
          onClick={(e) => {
            e.stopPropagation()

            if (contactId !== null && contactId !== undefined) {
              const contact = contactsStore.getItem(contactId)
              const timezone =
                contact instanceof Contact ? contact.timezone || contact.time_zone : ''
              const contactName =
                contact instanceof Contact
                  ? contact.first_name || contact.last_name || 'this contact'
                  : 'this contact'

              if (
                showCallDuringQuietHoursModalIfNeeded({
                  timezone,
                  contactName,
                  organizationQuietHoursSettings: organizationStore.quietHoursSettings,
                  onConfirm: () => callStore.connectTwilio(conversationId),
                })
              ) {
                return
              }
            }

            callStore.connectTwilio(conversationId)
          }}
          ariaLabel='CellContact_makeCall'
          className={className}
        />
      </Tooltip>
    )
  }
)
